import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { Truck, TruckStatus } from '../../types';
import { downloadTextFile, getCsvCell, makeImportId, parseCsvRecords, parseNumber, toCsv } from '../../utils/dataExchange';
import { formatDistance, getDistanceUnitLabel, getMileageCsvHeader, readMileageFromCsvRecord, toDisplayDistance, useDistanceUnit } from '../../utils/distance';

interface BulkOperationResult {
  success: boolean;
  processed: number;
  added: number;
  updated: number;
  errors: string[];
}

const truckStatuses: TruckStatus[] = ['In Service', 'Out for Repair', 'Needs Attention', 'Retired'];

const normalizeTruckStatus = (value: string): TruckStatus => {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliases: Record<string, TruckStatus> = {
    active: 'In Service',
    inservice: 'In Service',
    available: 'In Service',
    maintenance: 'Out for Repair',
    repair: 'Out for Repair',
    outofservice: 'Out for Repair',
    needsattention: 'Needs Attention',
    warning: 'Needs Attention',
    retired: 'Retired',
    inactive: 'Retired'
  };

  return aliases[normalized] || truckStatuses.find(status => status.toLowerCase() === value.toLowerCase()) || 'In Service';
};

const BulkVehicleManager: React.FC = () => {
  const { state, addTruck, updateTruck, deleteTruck } = useAppContext();
  const distanceUnit = useDistanceUnit();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkOperationResult | null>(null);
  const [selectedTrucks, setSelectedTrucks] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      const text = await file.text();
      const { records } = parseCsvRecords(text);
      const knownTrucks = [...state.trucks];
      const errors: string[] = [];
      const actor = state.currentUser?.id || 'bulk-import';
      const now = new Date();
      let added = 0;
      let updated = 0;

      if (records.length === 0) {
        setUploadResult({
          success: false,
          processed: 0,
          added: 0,
          updated: 0,
          errors: ['No vehicle rows were found in the CSV file.']
        });
        return;
      }

      for (let index = 0; index < records.length; index += 1) {
        const record = records[index];
        const rowNumber = index + 2;
        const id = getCsvCell(record, ['ID', 'Truck ID', 'Vehicle ID']);
        const vin = getCsvCell(record, ['VIN', 'Vehicle Identification Number']).toUpperCase();
        const licensePlate = getCsvCell(record, ['License Plate', 'Plate', 'Tag', 'Registration']);
        const make = getCsvCell(record, ['Make', 'Manufacturer']);
        const model = getCsvCell(record, ['Model']);
        const year = Math.trunc(parseNumber(getCsvCell(record, ['Year', 'Model Year']), 0));
        const mileage = readMileageFromCsvRecord(record, distanceUnit);
        const nickname = getCsvCell(record, ['Nickname', 'Name', 'Vehicle Name']);
        const status = normalizeTruckStatus(getCsvCell(record, ['Status', 'Vehicle Status']));

        const missingFields = [
          !vin && 'VIN',
          !licensePlate && 'License Plate',
          !make && 'Make',
          !model && 'Model',
          !year && 'Year'
        ].filter(Boolean);

        if (missingFields.length > 0) {
          errors.push(`Row ${rowNumber}: missing ${missingFields.join(', ')}.`);
          continue;
        }

        const existingTruck = knownTrucks.find(truck =>
          (id && truck.id === id) ||
          (vin && truck.vin.toLowerCase() === vin.toLowerCase()) ||
          (licensePlate && truck.licensePlate.toLowerCase() === licensePlate.toLowerCase())
        );

        if (existingTruck) {
          const updates: Partial<Truck> = {
            vin,
            licensePlate,
            make,
            model,
            year,
            mileage,
            nickname,
            status,
            updatedAt: now,
            updatedBy: actor
          };

          await updateTruck(existingTruck.id, updates);
          Object.assign(existingTruck, updates);
          updated += 1;
          continue;
        }

        const newTruck: Truck = {
          id: id || makeImportId('truck', rowNumber),
          vin,
          licensePlate,
          make,
          model,
          year,
          mileage,
          nickname,
          status,
          createdAt: now,
          updatedAt: now,
          createdBy: actor,
          updatedBy: actor
        };

        await addTruck(newTruck);
        knownTrucks.unshift(newTruck);
        added += 1;
      }
      
      setUploadResult({
        success: added + updated > 0,
        processed: added + updated,
        added,
        updated,
        errors
      });
    } catch (error) {
      setUploadResult({
        success: false,
        processed: 0,
        added: 0,
        updated: 0,
        errors: [`File processing error: ${error}`]
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExport = () => {
    const trucks = state.trucks;
    if (trucks.length === 0) {
      alert('No trucks to export');
      return;
    }

    const csvHeaders = [
      'ID',
      'VIN',
      'License Plate',
      'Make',
      'Model',
      'Year',
      getMileageCsvHeader(distanceUnit),
      'Nickname',
      'Status'
    ];

    const csvContent = toCsv(
      csvHeaders,
      trucks.map((truck: Truck) => [
        truck.id,
        truck.vin,
        truck.licensePlate,
        truck.make,
        truck.model,
        truck.year,
        Math.round(toDisplayDistance(truck.mileage, distanceUnit)),
        truck.nickname || '',
        truck.status
      ])
    );

    downloadTextFile(
      `fleet-export-${new Date().toISOString().split('T')[0]}.csv`,
      csvContent,
      'text/csv;charset=utf-8;'
    );
  };

  const handleBulkDelete = async () => {
    if (selectedTrucks.length === 0) {
      alert('Please select trucks to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedTrucks.length} truck(s)?`)) {
      return;
    }

    try {
      for (const truckId of selectedTrucks) {
        await deleteTruck(truckId);
      }
      setSelectedTrucks([]);
      alert(`Successfully deleted ${selectedTrucks.length} truck(s)`);
    } catch (error) {
      alert(`Error deleting trucks: ${error}`);
    }
  };

  const toggleTruckSelection = (truckId: string) => {
    setSelectedTrucks(prev => 
      prev.includes(truckId) 
        ? prev.filter(id => id !== truckId)
        : [...prev, truckId]
    );
  };

  const selectAllTrucks = () => {
    if (selectedTrucks.length === state.trucks.length) {
      setSelectedTrucks([]);
    } else {
  setSelectedTrucks(state.trucks.map((truck: Truck) => truck.id));
    }
  };

  const downloadTemplate = () => {
    const templateHeaders = [
      'VIN',
      'License Plate',
      'Make',
      'Model',
      'Year',
      getMileageCsvHeader(distanceUnit),
      'Nickname',
      'Status'
    ];

    downloadTextFile('fleet-import-template.csv', toCsv(templateHeaders, []), 'text/csv;charset=utf-8;');
  };

  return (
    <div className="bulk-vehicle-manager">
      <div className="page-header">
        <h1>Bulk Vehicle Management</h1>
        <p>Import, export, and manage multiple vehicles at once</p>
      </div>

      <div className="bulk-operations-grid">
        {}
        <div className="bulk-operation-card">
          <div className="card-header">
            <Upload className="card-icon" />
            <h3>Import Vehicles</h3>
          </div>
          <div className="card-content">
            <p>Upload a CSV file to import multiple vehicles at once.</p>
            <div className="upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="upload-button"
              >
                {isUploading ? 'Uploading...' : 'Choose CSV File'}
              </button>
              <button
                onClick={downloadTemplate}
                className="template-button"
              >
                <FileText size={16} />
                Download Template
              </button>
            </div>
            
            {uploadResult && (
              <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
                <div className="result-header">
                  {uploadResult.success ? (
                    <CheckCircle className="result-icon success" />
                  ) : (
                    <AlertCircle className="result-icon error" />
                  )}
                  <span>
                    {uploadResult.success 
                      ? `Import complete: ${uploadResult.added} added, ${uploadResult.updated} updated`
                      : 'Import failed'
                    }
                  </span>
                </div>
                {uploadResult.errors.length > 0 && (
                  <div className="error-list">
                    <h4>Errors:</h4>
                    <ul>
                      {uploadResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {}
        <div className="bulk-operation-card">
          <div className="card-header">
            <Download className="card-icon" />
            <h3>Export Vehicles</h3>
          </div>
          <div className="card-content">
            <p>Export all vehicle data to a CSV file for backup or external use.</p>
            <button
              onClick={handleExport}
              className="export-button"
              disabled={state.trucks.length === 0}
            >
              <Download size={16} />
              Export All Vehicles ({state.trucks.length})
            </button>
          </div>
        </div>

        {}
        <div className="bulk-operation-card">
          <div className="card-header">
            <Trash2 className="card-icon danger" />
            <h3>Bulk Delete</h3>
          </div>
          <div className="card-content">
            <p>Select multiple vehicles to delete them all at once.</p>
            <div className="bulk-actions">
              <button
                onClick={selectAllTrucks}
                className="select-all-button"
              >
                {selectedTrucks.length === state.trucks.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedTrucks.length === 0}
                className="bulk-delete-button"
              >
                <Trash2 size={16} />
                Delete Selected ({selectedTrucks.length})
              </button>
            </div>
          </div>
        </div>
      </div>

      {}
      {state.trucks.length > 0 && (
        <div className="vehicle-selection-table">
          <div className="table-header">
            <h3>Vehicle Selection</h3>
            <span>{selectedTrucks.length} of {state.trucks.length} selected</span>
          </div>
          <div className="table-container">
            <table className="bulk-manager-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedTrucks.length === state.trucks.length}
                      onChange={selectAllTrucks}
                    />
                  </th>
                  <th>License Plate</th>
                  <th>Make & Model</th>
                  <th>Year</th>
                  <th>Mileage ({getDistanceUnitLabel(distanceUnit)})</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {state.trucks.map((truck: Truck) => (
                  <tr key={truck.id} className={selectedTrucks.includes(truck.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedTrucks.includes(truck.id)}
                        onChange={() => toggleTruckSelection(truck.id)}
                      />
                    </td>
                    <td>{truck.licensePlate}</td>
                    <td>{truck.make} {truck.model}</td>
                    <td>{truck.year}</td>
                    <td>{formatDistance(truck.mileage, { unit: distanceUnit, includeUnit: false })}</td>
                    <td>
                      <span className={`status-badge ${truck.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {truck.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkVehicleManager;
